import React from 'react';
import Button from '../components/ui/Button';
import { resetFailedScreens } from './lazyPage';

// Catches a screen that could not be downloaded (see lazyPage.js).
//
// The usual cause is a new release: each release replaces the hashed files in
// /static/, so a visitor who opened the site before it and then moves to a screen
// they had not opened yet asks for a file that no longer exists. Reloading fetches
// the new release, so the first time this happens the page reloads itself (at most
// once every RELOAD_WINDOW_MS, so a file that is really missing can never cause a
// reload loop). After that, or when the browser blocks sessionStorage, the visitor
// sees a short message with a Reload button instead of a blank screen.
//
// Every other error is passed on unchanged, exactly as before code splitting.

export const RELOAD_KEY = 'mm:chunk-reload-at';
const RELOAD_WINDOW_MS = 30_000;

export const isChunkLoadError = (error) => Boolean(error) && (
  error.name === 'ChunkLoadError'
  || error.code === 'CSS_CHUNK_LOAD_FAILED'
  || /Loading (CSS )?chunk [^ ]+ failed/i.test(error.message || '')
);

// True (and remembered) when an automatic reload has not been tried recently.
const claimReload = () => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error) && claimReload()) {
      this.setState({ reloading: true });
      window.location.reload();
    }
  }

  componentDidUpdate(prevProps) {
    // Moving to another address (the Back button, a link) clears the message, and the
    // screen that failed is downloaded afresh the next time it is opened.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      resetFailedScreens();
      this.setState({ error: null, reloading: false });
    }
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    if (!isChunkLoadError(error)) throw error;
    if (reloading) return <div className="min-h-screen bg-brand-cream" />;
    return (
      <main role="alert" className="min-h-screen bg-brand-cream flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-2xl text-brand-ink mb-3">This page didn&rsquo;t load</h1>
          <p className="text-secondary text-body leading-relaxed mb-6">
            Your connection may have dropped, or we&rsquo;ve just released an update.
            Reloading usually fixes it. Your saved work is safe.
          </p>
          <Button variant="primary" onClick={() => window.location.reload()}>Reload the page</Button>
        </div>
      </main>
    );
  }
}

export default ChunkErrorBoundary;
